import { Fragment } from "react";

import Image from "next/image";

import type { SanityImageSource } from "@sanity/image-url";
import cn from "classnames";

import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { Price } from "@/components/price/price.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { RichText as RichTextType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { BUILD_PROTECTION_SURFACE_SHADOW } from "./buildProtection.constants";
import type {
  BuildProtectionBlock,
  BuildProtectionImage,
  BuildProtectionSystemStep,
} from "./buildProtection.types";
import { BuildProtectionCta } from "./buildProtectionCta.component";

export function BuildProtectionSystemOverview({
  steps,
  essentials,
  legalDisclaimer,
}: BuildProtectionSystemOverviewProps) {
  const items = steps ?? [];

  return (
    <div
      className="flex flex-col gap-12 lg:gap-16"
      data-testid="build-protection-system">
      {items.length > 0 ? (
        <>
          <ol className="hidden w-full items-start lg:flex">
            {items.map((step, index) => (
              <Fragment key={step._key}>
                {index > 0 ? (
                  <li
                    aria-hidden
                    className="bg-secondary/70 mt-[5.375rem] h-0.5 w-auto min-w-8 flex-1"
                  />
                ) : null}
                <li className="w-44 shrink-0">
                  <SystemStep step={step} />
                </li>
              </Fragment>
            ))}
          </ol>
          <CarouselCentered
            data-testid="build-protection-system-carousel"
            className="lg:hidden"
            mobileSlidesPerView={2}
            desktopSlidesPerView={2}
            mobileSpaceBetween={0}
            desktopSpaceBetween={0}
            centeredSlides={false}
            initialSlide={0}
            equalSlideHeights
            showNavigation>
            {items.map((step, index) => (
              <div key={step._key} className="relative w-full">
                {index < items.length - 1 ? (
                  <span
                    aria-hidden
                    className="bg-secondary/70 absolute top-[4.375rem] left-1/2 z-0 h-0.5 w-full"
                  />
                ) : null}
                <SystemStep step={step} className="relative z-10" />
              </div>
            ))}
          </CarouselCentered>
        </>
      ) : null}

      <BuildProtectionLegal
        value={legalDisclaimer}
        className="hidden lg:block"
      />

      {essentials?.title ? (
        <div
          data-testid="build-protection-essentials"
          className={`bg-primary-100 flex flex-col items-center gap-6 rounded-2xl px-6 py-7 text-center lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-8 lg:text-left ${BUILD_PROTECTION_SURFACE_SHADOW}`}>
          <div className="flex items-start gap-4 text-left">
            <EssentialsIcon icon={essentials.icon} title={essentials.title} />
            <div className="flex flex-col gap-1">
              <Text
                variant="subtitle1"
                component="h3"
                className="text-greyscale-headline">
                {essentials.title}
              </Text>
              {essentials.description ? (
                <Text
                  variant="body1"
                  component="p"
                  className="text-greyscale-body">
                  {essentials.description}
                </Text>
              ) : null}
            </div>
          </div>
          <div className="border-secondary/15 flex w-full flex-col items-center gap-4 lg:w-auto lg:flex-row lg:items-center lg:gap-6 lg:border-l lg:pl-6">
            <div className="flex flex-col items-center lg:items-start">
              {essentials.priceLabel ? (
                <Text
                  variant="body4"
                  component="p"
                  className="text-greyscale-body">
                  {essentials.priceLabel}
                </Text>
              ) : null}
              <Price
                value={essentials.price}
                primary
                className="text-greyscale-headline justify-center text-3xl font-bold lg:justify-start"
              />
            </div>
            <BuildProtectionCta
              variant="navy"
              label={essentials.ctaLabel}
              link={essentials.ctaLink}
              testId="build-protection-essentials-cta"
              className="w-full lg:w-auto"
            />
          </div>
        </div>
      ) : null}

      <BuildProtectionLegal value={legalDisclaimer} className="lg:hidden" />
    </div>
  );
}

function SystemStep({
  step,
  className,
}: {
  step: BuildProtectionSystemStep;
  className?: string;
}) {
  const copy = stepCopy(step);
  const image = step.product?.featuredImage?.url
    ? {
        src: step.product.featuredImage.url,
        alt: step.product.featuredImage.alt || copy.title || "System step",
      }
    : null;

  return (
    <div
      data-testid="build-protection-system-step"
      className={cn(
        "flex w-full flex-col items-center text-center",
        className,
      )}>
      <div className="relative px-3.5 pt-3.5">
        {step.badge ? (
          <span className="bg-secondary absolute top-0 left-0 z-10 flex size-14 items-center justify-center rounded-full px-1 text-center text-[9px] leading-tight font-bold text-white uppercase">
            {step.badge}
          </span>
        ) : null}
        <div className="border-greyscale-strength/20 flex size-28 items-center justify-center rounded-full border bg-white shadow-sm lg:size-36">
          {image ? (
            <Image
              src={image.src}
              alt={image.alt || copy.title}
              width={144}
              height={144}
              className="size-16 object-contain lg:size-20"
            />
          ) : null}
        </div>
      </div>
      {copy.title ? (
        <Text
          variant="h6"
          component="h3"
          className="text-greyscale-headline mt-6 lg:mt-8">
          {copy.title}
        </Text>
      ) : null}
      {copy.description ? (
        <Text
          variant="body4"
          component="p"
          className="text-greyscale-body mt-2">
          {copy.description}
        </Text>
      ) : null}
    </div>
  );
}

function EssentialsIcon({
  icon,
  title,
}: {
  icon?: BuildProtectionImage | null;
  title: string;
}) {
  const image = imageFromAsset(icon);
  if (!image) return null;

  return (
    <div className="bg-primary flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full">
      <Image
        src={image.src}
        alt={image.alt || title}
        width={48}
        height={48}
        className="size-7 object-contain"
      />
    </div>
  );
}

export function BuildProtectionLegal({
  value,
  className,
}: {
  value?: RichTextType | string | null;
  className?: string;
}) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  return (
    <div
      data-testid="build-protection-legal"
      className={cn("text-greyscale-body", className)}>
      {typeof value === "string" ? (
        <Text variant="body4" component="p" className="text-greyscale-body">
          {value}
        </Text>
      ) : (
        <RichText
          blockComponents={{
            normal: ({ children }) => (
              <Text
                variant="body4"
                component="p"
                className="text-greyscale-body">
                {children}
              </Text>
            ),
          }}>
          {value}
        </RichText>
      )}
    </div>
  );
}

const stepCopy = (step: BuildProtectionSystemStep) => ({
  title: step.title || step.product?.name || "",
  description: step.description || step.product?.shortDescription || "",
});

const imageFromAsset = (image?: BuildProtectionImage | null) => {
  if (!image?.asset) return null;
  return {
    src: urlFor(image as SanityImageSource)
      .width(280)
      .height(280)
      .url(),
    alt: image.alt || "",
  };
};

type BuildProtectionSystemOverviewProps = {
  steps?: BuildProtectionBlock["systemSteps"];
  essentials?: BuildProtectionBlock["essentials"];
  legalDisclaimer?: RichTextType | string | null;
};
